// React
import { Fragment, useEffect, useMemo, useState } from "react"

// Router
import { Routes } from "@/constants/routes"

// Queries
import { PublicProjectType, useProjectsStatsTableQuery } from "@/api/graphql"

// Translate
import { useTrans } from "@/i18n"

// Tables
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
	CellContext,
	createColumnHelper,
	SortingState,
} from "@/lib/table"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"

// UI
import { cn } from "@/lib/utils"
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { SearchInput } from "@/components/form-controls/Input"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"
import { Link } from "@/components/Anchor"
import { PageTemplate } from "@/layouts/PageTemplate"
import { DownloadSelector } from "@/components/DownloadSelector"
import { InvestmentOpportunityBanner } from "@/components/banners/InvestmentOpportunityBanner"

// Auth
import { AuthStateStatusEnum, useAuth } from "@/context/useAuth"

// CSV
import { exportToCsv, getCsvFileName } from "@/lib/csv"

// Types
import { DownloadableFileType } from "@/components/DownloadSelector/types"

// Constants
import {
	mapCoordinatesCenterOfNetherlands,
	ProjectBondsTypes,
} from "@/constants/constants"

/**
 * StatisticsProjects
 * @returns
 */
export function StatisticsProjects() {
	// State
	const [filter, setFilter] = useState<string | undefined>()
	const limit = 20
	const [currentPage, setCurrentPage] = useState<number>(0)
	const [sorting, setSorting] = useState<SortingState>([])
	const [isCsvLoading, setIsCsvLoading] = useState(false)

	// Translations
	const t = useTrans(["project", "common", "dashboard"])

	// Auth
	const auth = useAuth()

	// Query
	const { data, isLoading } = useProjectsStatsTableQuery({
		latitude: mapCoordinatesCenterOfNetherlands.lat,
		longitude: mapCoordinatesCenterOfNetherlands.lng,
		radius: "500",
		limit,
		offset: currentPage * limit,
		name__icontains: filter,
		type: ProjectBondsTypes,
	})

	// CSV download query - fetches all results when triggered
	const { data: csvData, isSuccess: isCsvDataReady } =
		useProjectsStatsTableQuery(
			{
				latitude: mapCoordinatesCenterOfNetherlands.lat,
				longitude: mapCoordinatesCenterOfNetherlands.lng,
				radius: "500",
				limit: null,
				offset: null,
				type: ProjectBondsTypes,
			},
			{ enabled: isCsvLoading },
		)

	// Trigger CSV export when data is ready
	useEffect(() => {
		if (
			!isCsvLoading ||
			!isCsvDataReady ||
			!csvData?.public_projects?.results
		) {
			return
		}

		const allProjects = csvData.public_projects.results.filter(
			Boolean,
		) as PublicProjectType[]
		const headers = [
			t("dashboard:stats.projects_table.header.project"),
			t("dashboard:stats.projects_table.header.start_date"),
			t("dashboard:stats.projects_table.header.payment_arrears"),
			t("dashboard:stats.projects_table.header.project_in_default"),
			t(
				"dashboard:stats.projects_table.header.project_in_default_explanation",
			),
		]
		exportToCsv({
			filename: getCsvFileName(t("dashboard:stats.projects_table.title")),
			data: allProjects.map((project) => {
				const row = project as Record<string, unknown>
				return {
					[headers[0]]: project.name,
					[headers[1]]: project.start,
					[headers[2]]: project.payment_arrears ?? "",
					[headers[3]]: row.project_in_default
						? t("common:project.project_in_default.true")
						: t("common:project.project_in_default.false"),
					[headers[4]]:
						(row.project_in_default_explanation_text as
							| string
							| undefined) ?? "",
				}
			}),
			headers,
		})
		setIsCsvLoading(false)
	}, [isCsvLoading, isCsvDataReady, csvData, t])

	function handleDownload(type: DownloadableFileType) {
		if (type === DownloadableFileType.Csv) {
			setIsCsvLoading(true)
		}
	}

	// Memo
	const projects = useMemo(() => {
		return (
			(data?.public_projects?.results?.filter(
				Boolean,
			) as PublicProjectType[]) ?? []
		)
	}, [data?.public_projects?.results])

	// PaginatorInfo
	const totalProjectsCount = useMemo(
		() => data?.public_projects?.totalCount ?? 0,
		[data?.public_projects?.totalCount],
	)

	// Tables
	const columnHelper = createColumnHelper<PublicProjectType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor((data) => data, {
				id: "name",
				header: () => (
					<TableHeading>
						{t("dashboard:stats.projects_table.header.project")}
					</TableHeading>
				),
				cell: (
					info: CellContext<PublicProjectType, PublicProjectType>,
				) => (
					<TableDataCell>
						<Link
							className={({ isActive }) =>
								cn(
									isActive && "active-navigation-item",
									"block truncate text-sm hover:text-gray-500",
								)
							}
							href={Routes.StatisticsPlatformSingleProject.replace(
								":id",
								info.getValue().id,
							)}
						>
							{info.getValue().name}
						</Link>
					</TableDataCell>
				),
			}),
			columnHelper.accessor("start", {
				header: () => (
					<TableHeading>
						{t("dashboard:stats.projects_table.header.start_date")}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, string>) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor((data) => data?.internal_rate_of_return, {
				id: "internal_rate_of_return",
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:stats.platform_stats.disclaimer.terms.current_expected_irr.text",
						)}
					>
						{t(
							"dashboard:stats.projects_table.header.current_expected_irr",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, number>) => (
					<TableDataCell className="font-medium">
						{info.getValue()
							? `${info.getValue().toFixed(2)}%`
							: null}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("payment_arrears", {
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:stats.platform_stats.disclaimer.terms.obligations.text",
						)}
					>
						{t(
							"dashboard:stats.projects_table.header.payment_arrears",
						)}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell className="font-medium">
						{info.getValue() ?? (
							<span className="text-gray-400">-</span>
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor(
				(row) =>
					(row as Record<string, unknown>).project_in_default as
						| boolean
						| undefined,
				{
					id: "project_in_default",
					header: () => (
						<TableHeading>
							{t(
								"dashboard:stats.projects_table.header.project_in_default",
							)}
						</TableHeading>
					),
					cell: (info) => {
						const value = info.getValue()
						return (
							<TableDataCell className="font-medium">
								{value
									? t(
											"common:project.project_in_default.true",
										)
									: t(
											"common:project.project_in_default.false",
										)}
							</TableDataCell>
						)
					},
				},
			),
			columnHelper.accessor(
				(row) =>
					(row as Record<string, unknown>)
						.project_in_default_explanation_text as
						| string
						| undefined,
				{
					id: "project_in_default_explanation_text",
					header: () => (
						<TableHeading>
							{t(
								"dashboard:stats.projects_table.header.project_in_default_explanation",
							)}
						</TableHeading>
					),
					cell: (info) => {
						const value = info.getValue()
						return (
							<TableDataCell multiline>
								{value ?? (
									<span className="text-gray-400">-</span>
								)}
							</TableDataCell>
						)
					},
				},
			),
		],
		[t],
	)

	// Table
	const table = useReactTable({
		columns,
		data: projects,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),

		enableHiding: true,
		state: {
			pagination: {
				pageIndex: 0,
				pageSize: limit,
			},
			sorting,
			columnVisibility: {
				// TODO: Re-enable these once calcs are fixed
				internal_rate_of_return: false,
			},
		},

		// Sorting
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		enableSorting: true,
	})

	// Data state
	return (
		<PageTemplate backHref={Routes.StatisticsPlatform}>
			{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
				<InvestmentOpportunityBanner className="mb-4" />
			) : null}
			<CardWrapper className="mb-4">
				<CardBody>
					<div className="md:flex">
						<Heading
							as="h2"
							styleAs="h5"
							className="mb-3 sm:truncate"
						>
							{t("dashboard:stats.projects_table.title")}
						</Heading>
						<div className="mb-4 ml-auto flex gap-4">
							<div className="flex gap-2 sm:ml-auto">
								<SearchInput
									onChange={(event) => {
										setFilter(event.currentTarget.value)
									}}
									label={"Zoeken"}
									className="md:width-auto min-w-full"
								/>
								<DownloadSelector
									options={[
										{
											name: t(
												"common:button.download.csv",
											),
											value: DownloadableFileType.Csv,
										},
									]}
									onDownload={handleDownload}
									disabled={isCsvLoading}
								/>
							</div>
						</div>
					</div>

					<Table
						className="min-w-[64rem] lg:min-w-0"
						data-testid="tablebody"
						isLoading={isLoading}
						headerGroups={table.getHeaderGroups()}
					>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => {
								return (
									<TableRowCell key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<Fragment key={header.id}>
													{flexRender(
														header.column.columnDef
															.header,
														header.getContext(),
													)}
												</Fragment>
											)
										})}
									</TableRowCell>
								)
							})}
						</TableHead>
						<TableBody
							data-testid="tablebody-overview"
							data-pageindex={
								table.getState().pagination.pageIndex
							}
						>
							{table.getRowModel().rows.map((row) => {
								const isOdd = row.index % 2 === 0
								return (
									<Fragment key={row.id}>
										<TableRowCell isOdd={isOdd}>
											{row
												.getVisibleCells()
												.map((cell, index) => {
													return (
														<Fragment key={index}>
															{flexRender(
																cell.column
																	.columnDef
																	.cell,
																cell.getContext(),
															)}
														</Fragment>
													)
												})}
										</TableRowCell>
									</Fragment>
								)
							})}
							{/* Pads the last entries in the table so the table doesn't collapse in the UI */}
							{table.getRowModel().rows.length <
								table.getState().pagination.pageSize &&
							table.getState().pagination.pageIndex !== 0 ? (
								<>
									{Array(
										Math.max(
											table.getState().pagination
												.pageSize -
												table.getRowModel().rows.length,
											1,
										),
									)
										.fill(true)
										.map((_, index) => (
											<TableRowCell
												key={index}
												withHover={false}
												isOdd={index % 2 === 0}
											>
												<TableDataCell
													colSpan={columns.length}
												>
													<span className="dummy-text" />
												</TableDataCell>
											</TableRowCell>
										))}
								</>
							) : null}
						</TableBody>
					</Table>
					<CardBody>
						{totalProjectsCount !== 0 && (
							<PaginationAsButtons
								countPerPage={limit}
								totalCount={totalProjectsCount ?? 0}
								itemType={
									"common:pagination.item_types.project"
								}
								currentPage={currentPage + 1}
								currentItemsAmount={totalProjectsCount ?? 0}
								onNextPage={() =>
									setCurrentPage(
										(currentPage) => currentPage + 1,
									)
								}
								onPrevPage={() =>
									setCurrentPage((currentPage) =>
										Math.max(currentPage - 1, 0),
									)
								}
								analyticsId="investments"
							/>
						)}
					</CardBody>
				</CardBody>
			</CardWrapper>
		</PageTemplate>
	)
}
